/**
 * Growth Intelligence: queries and helpers its pages share. The portal API owns the rules; the niche
 * profiles (topics, placeholders, notification rows, actions, disclaimers) are its configuration.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useOrganization } from "@/auth/organization";

import { portal } from "./api";
import type {
  GrowthBoardFilters,
  GrowthClientEvent,
  GrowthCompetitor,
  GrowthFrequency,
  GrowthInsightFilters,
  GrowthNiche,
  GrowthNicheCatalogue,
  GrowthProfile,
  GrowthSeverity,
  GrowthSourceType,
} from "./types";

/** The product's public name. PRD decision D-01 is still open: change it here. */
export const GROWTH_NAME = "Growth Intelligence";

/**
 * Whether the module's pages are reachable. While false, the sidebar entry stays but every
 * /growth-intelligence route shows the "coming soon" page. Flip to true to launch.
 */
export const GROWTH_LIVE = false;

/** The niche profiles onboarding offers. Configuration, so one fetch serves the whole session. */
export function useGrowthNiches() {
  return useQuery({ queryKey: ["growth-niches"], queryFn: portal.growthNiches, staleTime: Infinity });
}

export function findNiche(catalogue: GrowthNicheCatalogue | undefined, id: string | null | undefined) {
  return id ? catalogue?.niches.find((n) => n.id === id) : undefined;
}

/** How often a set-up business's profile is fetched again while the worker is busy on a competitor. */
export const SCAN_POLL_MS = 10_000;
/** A new competitor counts as being checked this long before the worker has queued anything for it. */
const STARTING_MS = 30 * 60_000;

/**
 * Whether the worker has work on a competitor: pages of its website still to capture, or AI analyses of
 * what they showed. A competitor added moments ago counts too, before its first pages are queued.
 */
export function inProgress(c: GrowthCompetitor, now: number = Date.now()): boolean {
  if (c.pages_to_check > 0 || c.analyses_to_run > 0) return true;
  return c.status === "pending" && c.last_checked_at === null && now - Date.parse(c.created_at) < STARTING_MS;
}

/** The competitors of a set-up business the worker is working on. */
export const working = (profile: GrowthProfile | undefined) =>
  profile?.status === "active" ? profile.competitors.filter((c) => inProgress(c)) : [];

/** The business's intelligence profile and set-up progress, kept fresh while the worker is busy. */
export function useGrowthProfile() {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: ["growth-profile", orgId],
    queryFn: () => portal.growthProfile(orgId),
    refetchInterval: (query) => (working(query.state.data).length > 0 ? SCAN_POLL_MS : false),
  });
}

/** The reload on a competitor's row: check its website now; the profile comes back with the work queued. */
export function useCheckCompetitor() {
  const { orgId } = useOrganization();
  const store = useStoreProfile();
  return useMutation({
    mutationFn: (competitorId: string) => portal.checkCompetitor(orgId, competitorId),
    onSuccess: store,
  });
}

/** Puts a profile the API just returned into the cache, so every screen sees the saved step at once. */
export function useStoreProfile(): (profile: GrowthProfile) => void {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  return useCallback((profile) => qc.setQueryData(["growth-profile", orgId], profile), [qc, orgId]);
}

/** "England, Scotland, Wales & NI — shown separately" (onboarding review, PRD F-04). */
export function jurisdictionScope(niche: GrowthNiche, catalogue: GrowthNicheCatalogue): string {
  const labels = niche.jurisdictions.map(
    (id) => catalogue.jurisdictions.find((j) => j.id === id)?.short_label ?? id,
  );
  const list = labels.length > 1 ? `${labels.slice(0, -1).join(", ")} & ${labels.at(-1)}` : (labels[0] ?? "");
  return niche.separate_jurisdictions && labels.length > 1 ? `${list} — shown separately` : list;
}

// ── notifications (onboarding step 6) ───────────────────────────────────────

export const FREQUENCIES: GrowthFrequency[] = ["immediate", "daily", "weekly"];

/** "High severity defaults to Immediate"; medium to a daily digest, low to a weekly one. */
export const DEFAULT_FREQUENCY: Record<GrowthSeverity, GrowthFrequency> = {
  high: "immediate",
  medium: "daily",
  low: "weekly",
};

const SEVERITY_ORDER: Record<GrowthSeverity, number> = { high: 0, medium: 1, low: 2 };

export interface NotificationRow {
  id: string;
  label: string;
  /** The row's most severe chosen topic. */
  severity: GrowthSeverity;
  /** The chosen topics the row's frequency applies to. */
  topics: string[];
  frequency: GrowthFrequency;
}

/**
 * The notification step's rows for the chosen topics, most severe first: one per topic, or the niche's
 * own groups ("Visa & sponsor policy"). Each starts at its saved frequency or its severity's default.
 */
export function notificationRows(
  niche: GrowthNiche,
  topics: string[],
  saved: Record<string, GrowthFrequency> | null,
): NotificationRow[] {
  const groups = niche.notification_rows.length
    ? niche.notification_rows
    : niche.topics.map((t) => ({ id: t.id, label: t.label, topics: [t.id] }));
  const rows: NotificationRow[] = [];
  for (const group of groups) {
    const chosen = niche.topics.filter((t) => group.topics.includes(t.id) && topics.includes(t.id));
    const [first] = chosen;
    if (!first) continue;
    const severity = chosen.reduce(
      (most, t) => (SEVERITY_ORDER[t.severity] < SEVERITY_ORDER[most] ? t.severity : most),
      first.severity,
    );
    rows.push({
      id: group.id,
      label: group.label,
      severity,
      topics: chosen.map((t) => t.id),
      frequency: saved?.[first.id] ?? DEFAULT_FREQUENCY[severity],
    });
  }
  // a stable sort keeps the niche's own order within a severity
  return rows.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

const DIGEST: Record<GrowthFrequency, string> = {
  immediate: "Immediate",
  daily: "Daily digest",
  weekly: "Weekly digest",
};

/** The review's notification summary: each severity's frequency, or "Mixed" when its rows differ. */
export function frequencyBySeverity(rows: NotificationRow[]): { severity: GrowthSeverity; label: string }[] {
  const out: { severity: GrowthSeverity; label: string }[] = [];
  for (const severity of ["high", "medium", "low"] as const) {
    const chosen = new Set(rows.filter((r) => r.severity === severity).map((r) => r.frequency));
    if (chosen.size === 0) continue;
    const [only] = [...chosen];
    out.push({ severity, label: chosen.size === 1 && only ? DIGEST[only] : "Mixed" });
  }
  return out;
}

// ── insights (PRD F-05 – F-08, F-11) ────────────────────────────────────────

/** Every insight query for the business sits under this key, so a mark refreshes them all. */
export function growthKey(orgId: string, ...rest: unknown[]) {
  return ["growth", orgId, ...rest];
}

/** The counts the dashboard and the Home card both show. */
export function useGrowthSummary() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: growthKey(orgId, "summary"), queryFn: () => portal.growthSummary(orgId) });
}

/** One page of the ranked feed. */
export function useGrowthInsights(filters: GrowthInsightFilters = {}) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: growthKey(orgId, "insights", filters),
    queryFn: () => portal.growthInsights(orgId, filters),
  });
}

/**
 * One insight with its evidence chain (3.9). While its interpretation is still being written (or rewritten
 * after a corrected answer), it's fetched again every few seconds so the page fills in by itself.
 */
export function useGrowthInsight(insightId: string) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: growthKey(orgId, "insight", insightId),
    queryFn: () => portal.growthInsight(orgId, insightId),
    refetchInterval: (query) => {
      const reading = query.state.data?.interpretation;
      return query.state.data && (!reading || reading.updating) ? SCAN_POLL_MS : false;
    },
  });
}

/** All insights (3.8): the ranked feed with the filter bar's choices, a page at a time ("Load N more"). */
export function useInsightPages(filters: GrowthInsightFilters, first: number, more: number) {
  const { orgId } = useOrganization();
  return useInfiniteQuery({
    queryKey: growthKey(orgId, "insights", "pages", filters, first, more),
    queryFn: ({ pageParam }) =>
      portal.growthInsights(orgId, {
        ...filters,
        page_token: pageParam ?? undefined,
        page_size: pageParam ? more : first,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_page_token,
  });
}

/** The person's own saved insights, most recently saved first. */
export function useGrowthSaved(pageSize: number) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: growthKey(orgId, "saved", pageSize),
    queryFn: () => portal.growthSaved(orgId, undefined, pageSize),
  });
}

/** Save or unsave an insight, then refresh every insight query (the counts and lists move together). */
export function useSaveInsight() {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saved }: { id: string; saved: boolean }) => portal.saveInsight(orgId, id, saved),
    onSettled: () => qc.invalidateQueries({ queryKey: growthKey(orgId) }),
  });
}

/** Where the business's intelligence comes from (the Sources page), with each source's status. */
export function useGrowthSources() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: growthKey(orgId, "sources"), queryFn: () => portal.growthSources(orgId) });
}

/** The recommendations board's cards and per-column counts. */
export function useGrowthBoard(filters: GrowthBoardFilters = {}) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: growthKey(orgId, "board", filters),
    queryFn: () => portal.growthBoard(orgId, filters),
  });
}

/** How often a card is fetched again while the AI is drafting its change. */
export const DRAFT_POLL_MS = 3_000;

/** One card with its proposed change and history, kept fresh while the AI drafts. */
export function useGrowthTask(taskId: string) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: growthKey(orgId, "task", taskId),
    queryFn: () => portal.growthTask(orgId, taskId),
    refetchInterval: (query) => (query.state.data?.draft?.status === "drafting" ? DRAFT_POLL_MS : false),
  });
}

/** The business's website and its published versions (from the website runtime). */
/** How often the website's versions are read again while a change waits to be published. */
export const PUBLISH_POLL_MS = 10_000;

export function useGrowthWebsite(opts: { poll?: boolean } = {}) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: growthKey(orgId, "website"),
    queryFn: () => portal.growthWebsite(orgId),
    refetchInterval: opts.poll ? PUBLISH_POLL_MS : false,
  });
}

/** "Last 30 days" as the start of that day, so the query stays the same all day. */
export function sinceDays(days: number | null, today: Date = new Date()): string | undefined {
  if (days === null) return undefined;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);
  return start.toISOString();
}

export const SOURCE_TYPE_LABEL: Record<GrowthSourceType, string> = {
  official: "Official",
  sector_body: "Sector body",
  public_web: "Public web",
  market_signal: "Market signal",
  local_authority: "Local authority",
};

/** A jurisdiction as a chip shows it ("UK", "NI", "Global"). */
export function jurisdictionChip(id: string, catalogue: GrowthNicheCatalogue | undefined): string {
  if (id === "uk") return "UK";
  if (id === "global") return "Global";
  return catalogue?.jurisdictions.find((j) => j.id === id)?.short_label ?? id;
}

/** A jurisdiction in full ("United Kingdom", "Northern Ireland", "Global"). */
export function jurisdictionName(id: string, catalogue: GrowthNicheCatalogue | undefined): string {
  if (id === "uk") return "United Kingdom";
  if (id === "global") return "Global";
  return catalogue?.jurisdictions.find((j) => j.id === id)?.label ?? id;
}

/**
 * Records a funnel event the browser alone sees (a niche picked, an insight opened). Fire and forget:
 * analytics never blocks or breaks the page. Values are ids and counts, never what people typed.
 */
export function useTrack(): (event: GrowthClientEvent) => void {
  const { orgId } = useOrganization();
  return useCallback(
    (event: GrowthClientEvent) => {
      portal.growthEvent(orgId, event).catch(() => undefined);
    },
    [orgId],
  );
}
